/**
 * SearchTermsPage — `/search-terms`. Реальные поисковые запросы из Директа:
 * расход/клики/конверсии. Кандидаты в минус-слова — расход без заявок.
 */

import { useMemo, useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { PeriodPicker, periodLabel, type Period } from '@/components/ui/PeriodPicker';
import { Badge, Button, Spinner, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { apiFetch } from '@/api/client';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useSearchTerms } from '@/hooks/api/useReports';

interface NegativesSuggestion {
  minusWords: string[];
  note: string;
  savedSpend: number;
}

function rub(n: number): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);
}

export function SearchTermsPage() {
  usePageHeader({ title: 'Поисковые запросы', subtitle: 'Расход по запросам + кандидаты в минус-слова' });

  const [period, setPeriod] = useState<Period>({ days: 10 });
  const [onlyWaste, setOnlyWaste] = useState(false);
  const { data, isLoading, isError, error, refetch } = useSearchTerms(period);
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();

  const terms = useMemo(() => {
    const all = data?.terms ?? [];
    return onlyWaste ? all.filter((t) => t.cost > 0 && t.leads === 0) : all;
  }, [data, onlyWaste]);

  const wasteTerms = useMemo(
    () => (data?.terms ?? []).filter((t) => t.cost > 0 && t.leads === 0),
    [data],
  );
  const wasteSpend = useMemo(() => wasteTerms.reduce((s, t) => s + t.cost, 0), [wasteTerms]);

  const [neg, setNeg] = useState<NegativesSuggestion | null>(null);
  const [negLoading, setNegLoading] = useState(false);
  const [negErr, setNegErr] = useState<string | null>(null);

  const loadNegatives = async () => {
    setNegLoading(true);
    setNegErr(null);
    try {
      const r = await apiFetch<NegativesSuggestion>('/api/ai/negatives', {
        method: 'POST',
        json: { terms: wasteTerms.slice(0, 60).map((t) => ({ query: t.query, cost: t.cost, clicks: t.clicks })) },
      });
      setNeg(r);
    } catch (e) {
      setNegErr((e as Error).message);
    } finally {
      setNegLoading(false);
    }
  };

  const applyNegatives = () => {
    openPanel();
    void sendMessage(
      `Добавь минус-слова по сливающим запросам за ${periodLabel(period)} (расход без заявок, экономия ≈ ${Math.round(wasteSpend)}₽). ` +
        (neg?.minusWords.length ? `Кандидаты: ${neg.minusWords.join(', ')}. ` : '') +
        `Прогони через /negatives (трёхслойная архитектура), не задень корп-семантику, и примени после моего апрува.`,
    );
  };

  return (
    <div className="col gap16" style={{ maxWidth: 1100 }}>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <div className="row center gap12">
          <button className={'btn btn-sm ' + (onlyWaste ? 'btn-primary' : 'btn-outline')} onClick={() => setOnlyWaste((v) => !v)}>
            Только «расход без заявок»
          </button>
          {wasteSpend > 0 && <Badge tone="b-danger">впустую ≈ {rub(wasteSpend)}</Badge>}
        </div>
        <div className="row center gap8">
          {wasteSpend > 0 && (
            <Button variant="primary" size="sm" icon="Sparkles" disabled={negLoading} onClick={() => void loadNegatives()}>
              {negLoading ? 'Подбираю…' : 'Подобрать минус-слова (AI)'}
            </Button>
          )}
          <PeriodPicker value={period} onChange={setPeriod} />
        </div>
      </div>

      {(negLoading || neg || negErr) && (
        <section className="card col" style={{ gap: 10, borderColor: 'var(--primary)' }}>
          <div className="row spread center">
            <h3 style={{ margin: 0 }}>🛠 Минус-слова → экономия</h3>
            {neg && <Badge tone="b-success">−{rub(neg.savedSpend)} / период</Badge>}
          </div>
          {negLoading && <div className="row center gap8 muted" style={{ fontSize: 13 }}><Spinner /> Анализирую запросы…</div>}
          {negErr && <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{negErr}</span>}
          {neg && (
            <>
              {neg.note && <p className="muted" style={{ margin: 0, fontSize: 13 }}>{neg.note}</p>}
              <div className="row wrap" style={{ gap: 6 }}>
                {neg.minusWords.map((w) => (
                  <span key={w} className="chip" style={{ background: 'var(--surface-alt)' }}>{w}</span>
                ))}
              </div>
              <div className="row">
                <Button variant="primary" size="sm" icon="ArrowRight" onClick={applyNegatives}>Применить в чате (через апрув)</Button>
              </div>
            </>
          )}
        </section>
      )}

      {isLoading && <LoadingBlock label="Загружаю поисковые запросы…" />}
      {isError && <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />}
      {!isLoading && !isError && data && (
        <section className="card">
          {!terms.length ? (
            <p className="muted" style={{ margin: 0 }}>Нет данных за период.</p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Запрос</th>
                  <th style={{ textAlign: 'right' }}>Расход</th>
                  <th style={{ textAlign: 'right' }}>Клики</th>
                  <th style={{ textAlign: 'right' }}>Заявки</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {terms.map((t) => {
                  const waste = t.cost > 0 && t.leads === 0;
                  return (
                    <tr key={t.query}>
                      <td>{t.query}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{rub(t.cost)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{t.clicks}</td>
                      <td className="tnum" style={{ textAlign: 'right', fontWeight: t.leads > 0 ? 700 : 400, color: t.leads > 0 ? 'var(--success-fg)' : undefined }}>
                        {t.leads || '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {waste && <Badge tone="b-warning">кандидат в минус</Badge>}
                        {t.leads > 0 && <Badge tone="b-success">конвертит</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
